/**
 * Modal that previews the captured video for a product-capture knowledge gap
 * and lets the user apply it to resolve the gap, or discard and recapture.
 *
 * Mounted from the "Capture ready" state card in GapDetail when
 *   summary.in_flight_capture.status === 'complete'
 *   && summary.in_flight_capture.video_ready
 *
 * On Apply: POST /resolve-with-product-capture; on success, invalidate the
 * standard three query keys and close. The gap re-renders with the
 * ResolvedBanner driven by the existing asset attribution columns.
 *
 * On Discard: DELETE /in-flight-capture; clears the linked session FK and
 * reverts status. Triggers parent to reopen LaunchCaptureModal if desired.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileVideo, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { toast } from '@/hooks/use-toast';
import knowledgeGapsService, {
  type KGSummaryResponse,
} from '@/lib/api/knowledgeGaps';
import contentLibraryService from '@/lib/api/contentLibrary';
import { patchKnowledgeGapSummaryCache } from '../queryCache';

export function CapturePreviewModal({
  open,
  onOpenChange,
  orgId,
  summary,
  onResolved,
  onDiscard,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orgId: string;
  summary: KGSummaryResponse;
  onResolved: (updated: KGSummaryResponse) => void;
  onDiscard: () => void;
}) {
  const queryClient = useQueryClient();
  const inFlight = summary.in_flight_capture;
  const organizationVideoId = inFlight?.organization_video_id ?? null;
  const [storageUrl, setStorageUrl] = useState<string | null>(null);

  // Fetch the captured video's playback URL. Reuses the existing content
  // library status endpoint — same one VideoPlayerDialog uses — so no new
  // signed-URL surface is required.
  const videoStatusQuery = useQuery({
    queryKey: ['kg-capture-video-status', organizationVideoId],
    queryFn: async () => {
      if (!organizationVideoId) return null;
      const response = await contentLibraryService.getVideoStatus(organizationVideoId);
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to load captured video');
      }
      return response.data;
    },
    enabled: open && Boolean(organizationVideoId),
  });

  useEffect(() => {
    if (videoStatusQuery.data?.storage_url) {
      setStorageUrl(videoStatusQuery.data.storage_url);
    }
  }, [videoStatusQuery.data?.storage_url]);

  const apply = useMutation({
    mutationFn: async () => {
      const response = await knowledgeGapsService.resolveWithProductCapture(
        orgId,
        summary.id,
      );
      if (!response.success || !response.data) {
        const message = response.error || 'Failed to apply capture to gap.';
        const err = new Error(message) as Error & { code?: string };
        err.code = response.code;
        throw err;
      }
      return response.data;
    },
    onSuccess: async (updated) => {
      await Promise.all([
        queryClient.cancelQueries({ queryKey: ['knowledge-gaps-summaries', orgId] }),
        queryClient.cancelQueries({
          queryKey: ['knowledge-gap-summary-detail', orgId, summary.id],
        }),
      ]);
      patchKnowledgeGapSummaryCache(queryClient, orgId, updated);
      queryClient.invalidateQueries({ queryKey: ['knowledge-gaps-summaries', orgId] });
      queryClient.invalidateQueries({
        queryKey: ['knowledge-gap-summary-detail', orgId, summary.id],
      });
      queryClient.invalidateQueries({ queryKey: ['knowledge-gaps-report', orgId] });
      toast({
        title: 'Gap resolved',
        description: 'The captured demo is now attached to this knowledge gap.',
      });
      onResolved(updated);
      onOpenChange(false);
    },
    onError: () => {
      toast({
        title: 'Apply failed',
        description: 'We could not apply this capture. Try again.',
        variant: 'destructive',
      });
    },
  });

  const discard = useMutation({
    mutationFn: async () => {
      const response = await knowledgeGapsService.cancelInFlightCapture(
        orgId,
        summary.id,
      );
      if (!response.success || !response.data) {
        const message = response.error || 'Failed to discard capture.';
        const err = new Error(message) as Error & { code?: string };
        err.code = response.code;
        throw err;
      }
      return response.data;
    },
    onSuccess: (updated) => {
      patchKnowledgeGapSummaryCache(queryClient, orgId, updated);
      queryClient.invalidateQueries({ queryKey: ['knowledge-gaps-summaries', orgId] });
      queryClient.invalidateQueries({
        queryKey: ['knowledge-gap-summary-detail', orgId, summary.id],
      });
      toast({
        title: 'Capture discarded',
        description: 'You can launch a new capture for this gap.',
      });
      onDiscard();
      onOpenChange(false);
    },
    onError: () => {
      toast({
        title: 'Discard failed',
        description: 'We could not discard this capture. Try again.',
        variant: 'destructive',
      });
    },
  });

  const isPending = apply.isPending || discard.isPending;

  return (
    <Dialog open={open} onOpenChange={(next) => !isPending && onOpenChange(next)}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileVideo className="h-5 w-5 text-primary" /> Captured demo
          </DialogTitle>
          <DialogDescription>
            Review the captured demo and apply it to resolve this gap, or
            discard and capture again.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="bg-black rounded-lg overflow-hidden aspect-video">
            {videoStatusQuery.isLoading ? (
              <div className="flex h-full items-center justify-center text-white">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            ) : storageUrl ? (
              <video controls className="h-full w-full" src={storageUrl}>
                Your browser does not support the video tag.
              </video>
            ) : (
              <div className="flex h-full items-center justify-center text-white">
                <div className="text-center">
                  <FileVideo className="mx-auto mb-3 h-12 w-12 opacity-50" />
                  <p className="text-sm">Captured video is not yet available.</p>
                </div>
              </div>
            )}
          </div>

          <div className="text-sm text-muted-foreground">
            <p className="font-medium text-foreground">{summary.topic}</p>
            {inFlight?.completed_at && (
              <p className="text-xs">
                Captured at {new Date(inFlight.completed_at).toLocaleString()}
              </p>
            )}
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            variant="ghost"
            onClick={() => discard.mutate()}
            disabled={isPending}
          >
            {discard.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Discarding…
              </>
            ) : (
              'Discard and recapture'
            )}
          </Button>
          <Button
            onClick={() => apply.mutate()}
            disabled={isPending || !storageUrl}
          >
            {apply.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Applying…
              </>
            ) : (
              'Apply to resolve gap'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
